import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Flame, Check, RotateCcw, Sparkles } from "lucide-react";
import { SectionHeading } from "../ui/SectionHeading";
import { Wave, Spark } from "../ui/decor";
import { useCountUp } from "../../hooks/useCountUp";
import { RATES } from "../../content/earnings";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Confetti-ish sparks for the day-7 celebration (no external lib). */
const CONFETTI = [
  { left: "8%", top: "12%", fill: "#FFC93C", delay: 0 },
  { left: "22%", top: "68%", fill: "#14B8A6", delay: 0.12 },
  { left: "40%", top: "6%", fill: "#8B5CF6", delay: 0.05 },
  { left: "60%", top: "70%", fill: "#FF6B5E", delay: 0.18 },
  { left: "78%", top: "14%", fill: "#14B8A6", delay: 0.1 },
  { left: "92%", top: "60%", fill: "#FFC93C", delay: 0.22 },
];

export function StreakWidget() {
  const reduced = useReducedMotion();
  const [checked, setChecked] = useState(0);
  const complete = checked >= DAYS.length;

  const banked = checked * RATES.timePer24h;
  const animatedBank = useCountUp(banked);

  const checkIn = () => setChecked((c) => Math.min(DAYS.length, c + 1));
  const reset = () => setChecked(0);

  return (
    <section id="streak" className="relative overflow-hidden bg-navy-950">
      {/* Melt the navy band into the cream page above and below. */}
      <Wave fill="#FFF6EC" flip />

      <div className="container-x py-16 sm:py-24">
        <SectionHeading
          light
          eyebrow="Show up. Get paid."
          title={
            <>
              Every day you check in{" "}
              <span className="marker text-navy">pays</span>
            </>
          }
          sub="500 calories land in your bank just for stepping on the scale — number moved or not. Tap through a week and watch the streak build."
        />

        <div className="mx-auto mt-14 max-w-3xl">
          <div className="card-pop relative bg-white p-6 sm:p-9">
            {/* Streak counter + running total */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="inline-flex items-center gap-2 rounded-full border-[3px] border-navy bg-sun px-4 py-1.5 shadow-pop-xs">
                <Flame className="h-5 w-5 text-coral" aria-hidden />
                <span className="font-display font-extrabold text-navy">
                  <span className="num">{checked}</span>-day streak
                </span>
              </div>
              <div className="text-right">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-charcoal/55">
                  Banked for showing up
                </p>
                <p className="num text-3xl font-extrabold text-teal">
                  {Math.round(animatedBank).toLocaleString()}
                  <span className="ml-1 text-sm font-bold text-charcoal/50">
                    cal
                  </span>
                </p>
              </div>
            </div>

            {/* Seven day-dots */}
            <div className="mt-8 grid grid-cols-7 gap-2 sm:gap-3">
              {DAYS.map((label, i) => {
                const lit = i < checked;
                return (
                  <div key={label} className="flex flex-col items-center gap-2">
                    <motion.div
                      animate={
                        lit && !reduced
                          ? { scale: [1, 1.25, 1] }
                          : { scale: 1 }
                      }
                      transition={{ duration: 0.35, ease: "easeOut" }}
                      className={`grid aspect-square w-full place-items-center rounded-full border-[3px] border-navy ${
                        lit ? "bg-sun shadow-pop-xs" : "bg-cream"
                      }`}
                    >
                      {lit ? (
                        i === checked - 1 ? (
                          <Flame
                            className="h-4 w-4 text-coral sm:h-5 sm:w-5"
                            aria-hidden
                          />
                        ) : (
                          <Check
                            className="h-4 w-4 text-navy sm:h-5 sm:w-5"
                            aria-hidden
                          />
                        )
                      ) : (
                        <span className="num text-xs font-bold text-charcoal/40">
                          {i + 1}
                        </span>
                      )}
                    </motion.div>
                    <span className="text-[11px] font-bold text-charcoal/55">
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Controls */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={checkIn}
                disabled={complete}
                className="btn-sun cursor-pointer px-7 py-3.5 text-base disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Check className="h-5 w-5" />
                {complete
                  ? "Week complete"
                  : checked === 0
                    ? "Check in"
                    : "Check in tomorrow"}
              </button>
              {checked > 0 && (
                <button
                  type="button"
                  onClick={reset}
                  className="btn-ghost cursor-pointer px-6 py-3"
                >
                  <RotateCcw className="h-4 w-4" /> Reset
                </button>
              )}
            </div>

            {/* Day-7 celebration */}
            <AnimatePresence>
              {complete && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="relative mt-7 overflow-hidden rounded-2xl border-[3px] border-navy bg-teal-mist p-5 text-center"
                >
                  {/* Confetti sparks */}
                  {!reduced &&
                    CONFETTI.map((c, i) => (
                      <motion.span
                        key={i}
                        aria-hidden
                        className="pointer-events-none absolute"
                        style={{ left: c.left, top: c.top }}
                        initial={{ opacity: 0, scale: 0, rotate: 0 }}
                        animate={{
                          opacity: [0, 1, 1, 0],
                          scale: [0, 1, 1, 0.6],
                          rotate: 180,
                          y: [0, -8, 6],
                        }}
                        transition={{
                          duration: 1.6,
                          delay: c.delay,
                          repeat: Infinity,
                          repeatDelay: 0.6,
                        }}
                      >
                        <Spark fill={c.fill} className="h-5 w-5" />
                      </motion.span>
                    ))}

                  <span className="sticker relative -rotate-2">
                    <Sparkles className="h-4 w-4 text-violet" />
                    7-day streak!
                  </span>
                  <p className="relative mt-3 font-display text-xl font-extrabold text-navy">
                    You banked{" "}
                    <span className="num text-teal">
                      {banked.toLocaleString()} calories
                    </span>{" "}
                    just for showing up.
                  </p>
                  <p className="relative mt-1 text-sm font-semibold text-charcoal/70">
                    That's the habit paying you back — same time tomorrow?
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <p className="mt-4 text-center text-xs font-semibold text-white/70">
            A taste of the in-app streak — real earnings follow your own scale.
          </p>
        </div>
      </div>

      <Wave fill="#FFF6EC" />
    </section>
  );
}
